import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Avatar,
  Stack,
  TextField,
  Tooltip
} from '@mui/material';
import {
  Check as ApproveIcon,
  Close as DenyIcon,
  Visibility as ViewIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Message as MessageIcon,
  Schedule as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as DeniedIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { teamRequestService, TeamRequestDTO } from '../services/teamRequestService';

interface TeamRequest {
  id: number;
  teamId: number;
  teamName: string;
  studentId: number;
  studentName: string;
  studentEmail: string;
  schoolId: string;
  message: string;
  status: 'PENDING' | 'APPROVED' | 'DENIED';
  requestedAt: string;
  processedAt?: string;
  processedBy?: string;
  processorName?: string;
  teamDepartment?: string;
  teamMemberCount?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`requests-tabpanel-${index}`}
      aria-labelledby={`requests-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const GroupRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<TeamRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState<TeamRequest | null>(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [actionDialog, setActionDialog] = useState<{ open: boolean; type: 'approve' | 'deny' | null; request: TeamRequest | null }>({
    open: false,
    type: null,
    request: null
  });
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [denyReason, setDenyReason] = useState('');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      
      // Load all pending requests using the API
      const pendingRequests = await teamRequestService.getPendingRequests();
      
      // For now, we're only showing pending requests from the API
      // TODO: Add endpoint to get all requests (pending + processed) if needed
      setRequests(pendingRequests);
    } catch (error) {
      console.error('Error loading requests:', error);
      toast.error('Failed to load team requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async () => {
    if (!actionDialog.request) return;

    try {
      setProcessingId(actionDialog.request.id);
      
      // Call the real API
      await teamRequestService.approveRequest(actionDialog.request.id);
      
      toast.success(`Approved ${actionDialog.request.studentName}'s request to join ${actionDialog.request.teamName}`);
      setActionDialog({ open: false, type: null, request: null });
      await loadRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error('Failed to approve request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDenyRequest = async () => {
    if (!actionDialog.request) return;

    try {
      setProcessingId(actionDialog.request.id);
      
      // Call the real API
      await teamRequestService.denyRequest(actionDialog.request.id);
      
      toast.success(`Denied ${actionDialog.request.studentName}'s request`);
      setActionDialog({ open: false, type: null, request: null });
      setDenyReason('');
      await loadRequests();
    } catch (error) {
      console.error('Error denying request:', error);
      toast.error('Failed to deny request');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Chip icon={<PendingIcon />} label="Pending" color="warning" size="small" />;
      case 'APPROVED':
        return <Chip icon={<ApprovedIcon />} label="Approved" color="success" size="small" />;
      case 'DENIED':
        return <Chip icon={<DeniedIcon />} label="Denied" color="error" size="small" />;
      default:
        return null;
    }
  };

  const filteredRequests = {
    pending: requests.filter(r => r.status === 'PENDING'),
    processed: requests.filter(r => r.status !== 'PENDING')
  };

  const renderRequestsTable = (requestsList: TeamRequest[]) => {
    if (requestsList.length === 0) {
      return (
        <Alert severity="info">
          No requests found in this category.
        </Alert>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell>Team Requested</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Request Date</TableCell>
              <TableCell align="center">Status</TableCell>
              {tabValue === 1 && <TableCell>Processed By</TableCell>}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requestsList.map((request) => (
              <TableRow key={request.id}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {request.studentName.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">{request.studentName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {request.schoolId} • {request.studentEmail}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{request.teamName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {request.teamMemberCount} members
                  </Typography>
                </TableCell>
                <TableCell>{request.teamDepartment}</TableCell>
                <TableCell>
                  {format(new Date(request.requestedAt), 'MMM dd, yyyy')}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {format(new Date(request.requestedAt), 'h:mm a')}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {getStatusChip(request.status)}
                </TableCell>
                {tabValue === 1 && (
                  <TableCell>
                    {request.processorName && (
                      <>
                        <Typography variant="body2">{request.processorName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.processedAt && format(new Date(request.processedAt), 'MMM dd, h:mm a')}
                        </Typography>
                      </>
                    )}
                  </TableCell>
                )}
                <TableCell align="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedRequest(request);
                          setViewDialog(true);
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    {request.status === 'PENDING' && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => setActionDialog({ open: true, type: 'approve', request })}
                            disabled={processingId === request.id}
                          >
                            {processingId === request.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <ApproveIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deny">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setActionDialog({ open: true, type: 'deny', request })}
                            disabled={processingId === request.id}
                          >
                            <DenyIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Group Access Requests
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Box display="flex" alignItems="center" gap={2}>
                <PendingIcon color="warning" />
                <Box>
                  <Typography variant="h4">{filteredRequests.pending.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Requests
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box display="flex" alignItems="center" gap={2}>
                <ApprovedIcon color="success" />
                <Box>
                  <Typography variant="h4">
                    {filteredRequests.processed.filter(r => r.status === 'APPROVED').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Approved This Month
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box display="flex" alignItems="center" gap={2}>
                <DeniedIcon color="error" />
                <Box>
                  <Typography variant="h4">
                    {filteredRequests.processed.filter(r => r.status === 'DENIED').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Denied This Month
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label={`Pending (${filteredRequests.pending.length})`} />
          <Tab label={`Processed (${filteredRequests.processed.length})`} />
        </Tabs>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TabPanel value={tabValue} index={0}>
            {renderRequestsTable(filteredRequests.pending)}
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {renderRequestsTable(filteredRequests.processed)}
          </TabPanel>
        </>
      )}

      {/* View Details Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Details</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Student Information</Typography>
                <Typography variant="body1">
                  {selectedRequest.studentName} ({selectedRequest.schoolId})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedRequest.studentEmail}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Team Requested</Typography>
                <Typography variant="body1">
                  {selectedRequest.teamName} - {selectedRequest.teamDepartment}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current members: {selectedRequest.teamMemberCount}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Request Message</Typography>
                <Typography variant="body1">
                  {selectedRequest.message}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                {getStatusChip(selectedRequest.status)}
              </Box>
              
              {selectedRequest.processedAt && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Processed</Typography>
                  <Typography variant="body1">
                    {selectedRequest.processorName} on {format(new Date(selectedRequest.processedAt), 'MMM dd, yyyy h:mm a')}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog 
        open={actionDialog.open} 
        onClose={() => setActionDialog({ open: false, type: null, request: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionDialog.type === 'approve' ? 'Approve Request' : 'Deny Request'}
        </DialogTitle>
        <DialogContent>
          {actionDialog.request && (
            <>
              <DialogContentText>
                {actionDialog.type === 'approve' 
                  ? `Are you sure you want to approve ${actionDialog.request.studentName}'s request to join ${actionDialog.request.teamName}?`
                  : `Are you sure you want to deny ${actionDialog.request.studentName}'s request to join ${actionDialog.request.teamName}?`
                }
              </DialogContentText>
              
              {actionDialog.type === 'deny' && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Reason for denial (optional)"
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value)}
                  sx={{ mt: 2 }}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, type: null, request: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionDialog.type === 'approve' ? 'success' : 'error'}
            onClick={actionDialog.type === 'approve' ? handleApproveRequest : handleDenyRequest}
            disabled={processingId !== null}
            startIcon={processingId !== null && <CircularProgress size={20} />}
          >
            {actionDialog.type === 'approve' ? 'Approve' : 'Deny'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GroupRequestsPage;